import z from "zod";
import { mapErrorsToFields } from "../../../../utils";
import OccasionCollectionService from "../../../../services/OccasionCollectionService";

const api = new OccasionCollectionService();

export type State = {
  message: string | null;
  errors?: {
    name?: string[];
    general?: string[];
  };
  success: boolean;
  defaultValues: {
    name?: string;
  };
};

const FormSchema = z.object({
  name: z.string().min(1, { message: "Name is required." }),
});

export async function createOccasionCollection(
  prevState: State,
  formData: FormData
): Promise<State> {
  const defaultValues = {
    name: formData.get("name")?.toString() || "",
  };
  try {
    const validatedFields = FormSchema.safeParse(defaultValues);
    if (!validatedFields.success) {
      return {
        errors: mapErrorsToFields(validatedFields.error),
        message: "Failed to create occasion collection.",
        defaultValues,
        success: false,
      };
    }
    const result = await api.createOccasionCollection(validatedFields.data);
    if (result.status === 201)
      return {
        errors: {},
        message: "Occasion collection created successfully.",
        defaultValues,
        success: true,
      };
    else if (result.status === 400 || result.status === 409)
      return {
        errors: {},
        message: result.data.description,
        defaultValues,
        success: false,
      };
  } catch (error) {
    console.error("Error creating occasion collection:", error);
  }
  return {
    errors: {},
    message: "Failed to create occasion collection.",
    defaultValues,
    success: false,
  };
}

export async function updateOccasionCollection(
  id: number,
  prevState: State,
  formData: FormData
): Promise<State> {
  const defaultValues = {
    name: formData.get("name")?.toString() || "",
  };
  try {
    const validatedFields = FormSchema.safeParse(defaultValues);
    if (!validatedFields.success) {
      return {
        errors: mapErrorsToFields(validatedFields.error),
        message: "Failed to update occasion collection.",
        defaultValues,
        success: false,
      };
    }
    const result = await api.updateOccasionCollection({
      id: id,
      ...validatedFields.data,
    });
    if (result.status === 204)
      return {
        errors: {},
        message: "Occasion collection updated successfully.",
        defaultValues,
        success: true,
      };
  } catch (error) {
    console.error("Error updating occasion collection:", error);
  }
  return {
    errors: {},
    message: "Failed to update occasion collection.",
    defaultValues,
    success: false,
  };
}

export async function deleteOccasionCollection(
  id: number
): Promise<{ success: boolean }> {
  try {
    const result = await api.deleteOccasionCollection(id);
    return { success: result.status === 204 };
  } catch (error) {
    console.error("Database Error:", error);
  }
  return { success: false };
}
